import { type GenericMap, type OptionItem, type Rect } from "@client/types";
import { type WithChildren } from "@client/types/WithChildren";
import { isBrowser, isDev } from "@common/lib/misc";
import { getIsIframed } from "@common/lib/url";
import { isUndefined, omit, pick } from "lodash";
import moment from "moment";
import React from "react";
import {
  type InferableComponentEnhancerWithProps,
  type MapDispatchToPropsParam,
  type MapStateToPropsParam,
  ReactReduxContext,
  connect
} from "react-redux";
import { type Reducer, type ReducersMapObject, combineReducers as reduxCombineReducers } from "redux";
import { parseQueryString } from "./api";
export { isBrowser, isDev, labelize } from "@common/lib/misc";

/* Copied from old redux TS */
/**
 * Internal "virtual" symbol used to make the `CombinedState` type unique.
 */
declare const $CombinedState: unique symbol;

/**
 * State base type for reducers created with `combineReducers()`.
 *
 * This type allows the `createStore()` method to infer which levels of the
 * preloaded state can be partial.
 *
 * Because Typescript is really duck-typed, a type needs to have some
 * identifying property to differentiate it from other types with matching
 * prototypes for type checking purposes. That's why this type has the
 * `$CombinedState` symbol property. Without the property, this type would
 * match any object. The symbol doesn't really exist because it's an internal
 * (i.e. not exported), and internally we never check its value. Since it's a
 * symbol property, it's not expected to be unumerable, and the value is
 * typed as always undefined, so its never expected to have a meaningful
 * value anyway. It just makes this type distinquishable from plain `{}`.
 */
interface EmptyObject {
  readonly [$CombinedState]?: undefined;
}
export type CombinedState<S> = EmptyObject & S;

/* END OF: Copied from old redux TS */

export const evalInContext = (context: object, js: string) => {
  return function () {
    try {
      return eval(js);
    } catch (e) {
      console.error(e, js);
    }
  }.call(context);
};

export const areRectsEqual = (a?: Rect, b?: Rect) => {
  if (a === b) return true;
  if (!a || !b) return false;

  return (
    a.right === b.right &&
    a.left === b.left &&
    a.bottom === b.bottom &&
    a.height === b.height &&
    a.top === b.top &&
    a.width === b.width
  );
};

export const isDefined = (val: any): boolean => typeof val !== "undefined";
export const ifDefined = (val: any, def: any) => (isDefined(val) ? val : def);

export const scrollToY = (y: number, delay: number) =>
  $("body,html").stop(true).animate(
    {
      scrollTop: y
    },
    delay
  );

export const scrollToElement = (element: HTMLElement) =>
  scrollToY(element.getBoundingClientRect().top + window.scrollY, 300);

export const devToolsExtension =
  isDev() && isBrowser() && window.__REDUX_DEVTOOLS_EXTENSION__ ? window.__REDUX_DEVTOOLS_EXTENSION__() : (_) => _;

export const combineReducers = <S>(reducers: ReducersMapObject<S, any>) =>
  Object.keys(reducers).length ? reduxCombineReducers(reducers) : (state = {} as CombinedState<S>) => state;

export const combineMultipleReducers = <S>(reducers: GenericMap<any>): GenericMap<Reducer<CombinedState<S>>> =>
  Object.keys(reducers).reduce(
    (res, r) => ({
      ...res,
      [r]: combineReducers(reducers[r])
    }),
    {}
  );

export const atLeastIn = (promise: Promise<any>, inMS: number): Promise<any> =>
  new Promise((resolve, reject) => {
    setTimeout(() => promise.then(resolve).catch(reject), inMS);
  });

export const addImportant = (css: React.CSSProperties): React.CSSProperties =>
  Object.keys(css).reduce((res, key) => ({ ...res, [key]: `${css[key]} !important` }), {});

export const delay = (fn: () => Promise<any>, inMS: number): Promise<any> =>
  new Promise((resolve, reject) => {
    setTimeout(() => fn().then(resolve).catch(reject), inMS);
  });

const UNIT_TO_MS = {
  ms: 1,
  s: 1000,
  m: 60 * 1000,
  h: 60 * 60 * 1000
};
export const toMilliseconds = (time: string): number => {
  const match = time.match(/^([\d.]+)(ms|s|m|h)$/);
  if (!match) return parseFloat(time);
  return +match[1] * UNIT_TO_MS[match[2]];
};

export const setCustomTimeout = (fn: () => void, delay: string) => setTimeout(fn, toMilliseconds(delay));

export const waitUntil = (conditionFn: () => boolean, action: () => void, checkIntervalMs: number) => {
  if (conditionFn()) {
    action();
  } else {
    const interval = setInterval(() => {
      if (conditionFn()) {
        clearInterval(interval);
        action();
      }
    }, checkIntervalMs);
  }
};

export const toOptionItem = (value?: string | OptionItem): OptionItem | undefined => {
  if (isUndefined(value)) return;
  if (typeof value === "string") return { label: value, value };
  return value;
};

export const toOptionItems = (items?: Array<string | OptionItem>): OptionItem[] =>
  (items || []).map((it) => (typeof it === "string" ? { label: it, value: it } : it));

export const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
export const EMPTY_OBJECT = {};

export const formatDate = (value: Date | string, format: string): string => moment(value).format(format);

interface ConnectOptions<State = unknown, TStateProps = {}, TOwnProps = {}, TMergedProps = {}> {
  forwardRef?: boolean;
  context?: typeof ReactReduxContext;
  areStatesEqual?: (nextState: State, prevState: State, nextOwnProps: TOwnProps, prevOwnProps: TOwnProps) => boolean;
  areOwnPropsEqual?: (nextOwnProps: TOwnProps, prevOwnProps: TOwnProps) => boolean;
  areStatePropsEqual?: (nextStateProps: TStateProps, prevStateProps: TStateProps) => boolean;
  areMergedPropsEqual?: (nextMergedProps: TMergedProps, prevMergedProps: TMergedProps) => boolean;
}
export const connectCustom = <
  TStateProps = {},
  TDispatchProps = {},
  TOwnProps = Partial<WithChildren>,
  State = unknown
>(
  mapStateToProps?: MapStateToPropsParam<TStateProps, TOwnProps, State> | null,
  mapDispatchToProps?: MapDispatchToPropsParam<TDispatchProps, TOwnProps> | null,
  options?: ConnectOptions<State, TStateProps, TOwnProps>
): InferableComponentEnhancerWithProps<
  TStateProps & Omit<TDispatchProps, "dispatch"> & Partial<WithChildren>,
  TOwnProps
> =>
  // TODO: TS issues
  connect<TStateProps, TDispatchProps, TOwnProps, State>(
    mapStateToProps as any,
    mapDispatchToProps as any,
    (stateProps: TStateProps, dispatchProps: TDispatchProps, ownProps: TOwnProps) => ({
      ...stateProps,
      ...omit(dispatchProps as any, "dispatch"),
      ...pick(ownProps, "children")
    }),
    options as any
  );

export const toCamelCase = (name: string): string =>
  name.replace(/^([A-Z]+)/, (_, a) =>
    a.length === 1 ? a.toLowerCase() : a.substr(0, a.length - 1).toLowerCase() + a[a.length - 1]
  );

export const getReturnUrl = (url?: string): string | null => {
  const qs = parseQueryString(url);
  const key = Object.keys(qs).find((x) => x.toLowerCase() === "returnurl");
  return key ? qs[key] : null;
};

export const joinDefined = (separator: string, ...args: Array<string | null | undefined>) =>
  args.filter((x) => !!x).join(separator);

export type IframeExitReason = "membership_disabled";
export type IFrameExitArgs = {
  programId?: string;
  reason: IframeExitReason;
};
export const exitIframe = (args?: IFrameExitArgs) => {
  window.parent.postMessage({ type: "WSC_EXIT", args }, "*");
};

export const isVisible = (visibilityCondition: string | null | undefined, values: GenericMap<any>): boolean => {
  if (visibilityCondition) {
    const isVisible = evalInContext({ f: values }, visibilityCondition.replace(/f\./g, "this.f."));
    if (!isUndefined(isVisible) && !isVisible) return false;
  }
  return true;
};

export const setOgMetaTagContent = (name: string, content: string | null | undefined) => {
  let tag = document.head.querySelector(`meta[property='og:${name}']`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", `og:${name}`);

    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content ?? "");
};

export const setMetaTagContent = (name: string, content: string | null | undefined) => {
  let tag = document.head.querySelector(`meta[name='${name}']`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);

    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content ?? "");
};

const addComment = (name: string, location: HTMLElement, isAppend: boolean, element: Comment | null): Comment => {
  const node = document.createComment(name);
  if (element) {
    if (isAppend) {
      element.after(node);
    } else {
      element.before(node);
    }
  } else {
    if (isAppend) {
      location.append(node);
    } else {
      location.prepend(node);
    }
  }
  return node;
};

const getComment = (name: string, location: HTMLElement): Comment | null => {
  const iterator = document.createNodeIterator(location, NodeFilter.SHOW_COMMENT, () => NodeFilter.FILTER_ACCEPT);

  let curNode: Node | null = null;
  while ((curNode = iterator.nextNode())) {
    if (curNode.nodeValue?.trim().toLowerCase() === name.toLowerCase()) return curNode as Comment;
  }

  return null;
};

const removeContentBetweenComments = (start: Comment, end: Comment) => {
  let sibling = start.nextSibling;
  while (sibling && sibling !== end) {
    sibling.parentElement?.removeChild(sibling);

    sibling = start.nextSibling;
  }
};

export const setCustomContent = (
  name: string,
  location: HTMLElement,
  isAppend: boolean,
  content: string | null | undefined
) => {
  let startCommentNode = getComment(`Start: ${name}`, location);
  let endCommentNode = getComment(`End: ${name}`, location);
  if (content) {
    if (!startCommentNode) {
      startCommentNode = addComment(`Start: ${name}`, location, isAppend, null);
    }

    if (!endCommentNode) {
      endCommentNode = addComment(`End: ${name}`, location, true, startCommentNode!);
    }

    removeContentBetweenComments(startCommentNode, endCommentNode);

    $(startCommentNode).after(content);
  } else {
    if (startCommentNode && endCommentNode) {
      removeContentBetweenComments(startCommentNode, endCommentNode);
    }

    startCommentNode && startCommentNode.parentNode?.removeChild(startCommentNode);
    endCommentNode && endCommentNode.parentNode?.removeChild(endCommentNode);
  }
};

export const getUserDisplayName = (data: {
  firstName?: string | null;
  lastName?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
}): string => {
  return joinDefined(" ", data.firstName ?? data.userFirstName, data.lastName ?? data.userLastName) ?? "";
};

export const processInIframe = () => {
  if (getIsIframed()) {
    document.body.style.background = "#fff";
  }
};
